// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Tab } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  AppointmentClientDetailTab,
  ContactClientDetailTab,
  CostClientDetailTab,
  DocumentClientDetailTab,
  HistoryClientDetailTab,
  InfoClientDetailTab,
  InvoiceClientDetailTab,
  QuoteClientDetailTab,
  TaskClientDetailTab,
} from '~/components/module/client';
import { BaseTabList, BaseToolbar } from '~/components/common';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseTabItem } from '~/utils/interface/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { ClientTabEnum, RoleEnum, TypeCrmEnum } from '~/utils/enum/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './ClientDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ClientDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { id: clientId } = useParams();
  const [searchParams] = useSearchParams();
  const typeList = searchParams.get('type');

  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: ClientTabEnum.DETAILS, component: <InfoClientDetailTab /> }];

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.CONTACTS, component: <ContactClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.ORDERS, component: <QuoteClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.APPOINTMENTS, component: <AppointmentClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT) items.push({ label: ClientTabEnum.TASKS, component: <TaskClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.DOCUMENTS, component: <DocumentClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.INVOICE, component: <InvoiceClientDetailTab /> });

    if (typeList === TypeCrmEnum.CLIENT)
      items.push({ label: ClientTabEnum.HISTORY, component: <HistoryClientDetailTab /> });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(ClientTabEnum.DETAILS);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate({
      pathname,
      search,
      hash: `#${newValue}`,
    });
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    if (!typeList) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(typeList === TypeCrmEnum.CLIENT ? adminRouteAbsolute.clients : adminRouteAbsolute.leads);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(typeList === TypeCrmEnum.CLIENT ? salesAdminRouteAbsolute.clients : salesAdminRouteAbsolute.leads);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(typeList === TypeCrmEnum.CLIENT ? salesPersonRouteAbsolute.clients : salesPersonRouteAbsolute.leads);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          typeList === TypeCrmEnum.CLIENT
            ? operationsManagerRouteAbsolute.clients
            : operationsManagerRouteAbsolute.leads
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          typeList === TypeCrmEnum.CLIENT ? warehouseLeadRouteAbsolute.clients : warehouseLeadRouteAbsolute.leads
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          typeList === TypeCrmEnum.CLIENT ? warehouseStaffRouteAbsolute.clients : warehouseStaffRouteAbsolute.leads
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(typeList === TypeCrmEnum.CLIENT ? foremanRouteAbsolute.clients : foremanRouteAbsolute.leads);
        break;
      case RoleEnum.CREW:
        navigate(typeList === TypeCrmEnum.CLIENT ? crewRouteAbsolute.clients : crewRouteAbsolute.leads);
        break;
      case RoleEnum.LOADER:
        navigate(typeList === TypeCrmEnum.CLIENT ? loaderRouteAbsolute.clients : loaderRouteAbsolute.leads);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='clientDetailPage' className={cx('container')}>
      <BaseToolbar
        title={t(typeList === TypeCrmEnum.CLIENT ? 'client_detail_title' : 'lead_detail_title')}
        isBack
        onBackToPreviousPage={handleOnBack}
      />

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            label={t(`tab_label_${item.label}`)}
            className={cx('tabItem', { active: tabKey === item.label })}
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientDetail;
